import { useState, useCallback } from 'react';
import apiFetch from '@scale/frontend-lib/api-fetch';
import { AutoCritiqueRequest } from '@scale/shared/types/chat';

export type AutoCritiqueHook = ReturnType<typeof useAutoCritique>;

export type AutoCritiqueOutput = {
  severity: AutoCritiqueSeverity;
  critique: string;
  loading: boolean;
};

export enum AutoCritiqueSeverity {
  None = 'none',
  Minor = 'minor',
  Moderate = 'moderate',
  Major = 'major',
  Critical = 'critical',
  Missing = 'missing',
}

export const mapAutoCritiqueSeverityToVariant = (severity: AutoCritiqueSeverity) => {
  switch (severity) {
    case AutoCritiqueSeverity.None:
      return 'info';
    case AutoCritiqueSeverity.Minor:
      return 'info';
    case AutoCritiqueSeverity.Moderate:
      return 'warning';
    case AutoCritiqueSeverity.Major:
      return 'danger';
    case AutoCritiqueSeverity.Critical:
      return 'danger';
    case AutoCritiqueSeverity.Missing:
      return 'danger';
    default:
      return 'info';
  }
};

const ERROR_MESSAGE =
  'There was an error fetching the critique. Please wait a few seconds and try again.';

const SEVERITY_FORMAT = 'Severity:';

/**
 * Determines the severity level of an AutoCritique response. Returns None if the expected
 * severity format is guaranteed to not exist at all in the response. Returns Missing if
 * there is not enough information yet to determine the severity level due to streaming.
 * Otherwise, returns the parsed severity level.
 *
 * @param critique - The AutoCritique response to evaluate.
 * @returns The parsed severity level from the AutoCritique response.
 */
const getSeverityLevel = (critique: string): AutoCritiqueSeverity => {
  if (critique === ERROR_MESSAGE) {
    return AutoCritiqueSeverity.Missing;
  }
  const lines = critique.split('\n');
  if (lines.length === 0) {
    return AutoCritiqueSeverity.Missing;
  }
  const firstLine = lines[0];
  if (!firstLine.startsWith(SEVERITY_FORMAT)) {
    if (firstLine.length >= SEVERITY_FORMAT.length) {
      return AutoCritiqueSeverity.None;
    }
    return AutoCritiqueSeverity.Missing;
  }
  const severity = firstLine.split(':')[1].trim().toLowerCase();
  if (Object.values(AutoCritiqueSeverity).includes(severity as AutoCritiqueSeverity)) {
    return severity as AutoCritiqueSeverity;
  }
  return AutoCritiqueSeverity.Missing;
};

const useAutoCritique = (response?: string) => {
  const [critiqueData, setCritiqueData] = useState<{
    severity: AutoCritiqueSeverity;
    critique: string;
    loading: boolean;
  }>({
    severity: response ? getSeverityLevel(response) : AutoCritiqueSeverity.Missing,
    critique: response || '',
    loading: false,
  });

  const getBlockOnCritiqueSeverity = useCallback(
    (response: string, blockCriticalSeverity: boolean, regexToBlock: string) => {
      if (regexToBlock) {
        return new RegExp(regexToBlock).test(response);
      } else if (blockCriticalSeverity) {
        return [AutoCritiqueSeverity.Critical, AutoCritiqueSeverity.Major].includes(
          getSeverityLevel(response),
        );
      }
      return false;
    },
    [],
  );

  const getAutoCritique: (params: AutoCritiqueRequest) => Promise<AutoCritiqueOutput> = useCallback(
    async (params: AutoCritiqueRequest) => {
      setCritiqueData({ severity: AutoCritiqueSeverity.Missing, critique: '', loading: true });
      let responseText = '';
      return new Promise(resolve => {
        try {
          apiFetch(null, 'internal/genai/autoCritique', {
            method: 'POST',
            body: params,
            fetchEventSourceOptions: {
              openWhenHidden: true,
              onopen: async response => {
                if (!response.ok) {
                  throw new Error('Failed to get model response. Please try again.');
                }
              },
              onmessage: event => {
                if (event.data === '[DONE]' || event.data === '[IGNORE]') {
                  return;
                }
                const parsed = JSON.parse(event.data);
                if (parsed.error) {
                  throw new Error(parsed.error);
                }
                if (parsed.fullOutput) {
                  responseText = parsed.fullOutput;
                } else if (parsed.output) {
                  responseText += parsed.output;
                } else if (parsed.choices?.[0]?.delta?.content) {
                  responseText += parsed.choices[0].delta.content as string;
                } else if (
                  'type' in parsed &&
                  parsed.type === 'content_block_delta' &&
                  parsed.delta?.text
                ) {
                  responseText += parsed.delta.text;
                }
                setCritiqueData({
                  severity: getSeverityLevel(responseText),
                  critique: responseText,
                  loading: true,
                });
              },
              onclose: () => {
                setCritiqueData({
                  severity: getSeverityLevel(responseText),
                  critique: responseText,
                  loading: false,
                });
                resolve({
                  severity: getSeverityLevel(responseText),
                  critique: responseText,
                  loading: false,
                });
              },
              onerror: e => {
                throw e;
              },
            },
          });
        } catch (e) {
          setCritiqueData({
            severity: AutoCritiqueSeverity.Missing,
            critique: ERROR_MESSAGE,
            loading: false,
          });
          resolve({
            severity: AutoCritiqueSeverity.Missing,
            critique: ERROR_MESSAGE,
            loading: false,
          });
        }
      });
    },
    [],
  );

  const clearCritiqueData = useCallback(() => {
    setCritiqueData({ severity: AutoCritiqueSeverity.Missing, critique: '', loading: false });
  }, []);

  return {
    critiqueData,
    getBlockOnCritiqueSeverity,
    getAutoCritique,
    clearCritiqueData,
  };
};

export default useAutoCritique;
